import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { authAxios, QueryStatus } from "../../../utils";
import { RootState } from "../../../redux/store";
import {
  AdvancedAnalyticsVehicles,
  AdvancedAnalyticsVehiclesApi,
  DriversHistory,
  MonthlyReportApi,
} from "./fleetApi";
import {
  getAdditionalCostEndContract,
  getConsumedContractDurationMonth,
  getContractDurationMonth,
  getContractMensualMileage,
  getMensualMileage,
  getMileageDeviationPercentage,
  getMileageVariationEndContract,
  getMonthlyMileageDeviation,
  getProjectionEndOfContract,
  getRemainingContractDurationMonth,
} from "./lib";
import { capitalize } from "@qivia/ui";
import { MONTHLY_REPORTS_MOCK } from "./monthlyReports/monthlyReportsMock";
import { PlanningsMonthlyRowsByCompanyResponse } from "@qivia/schemas-cards-drivers-plannings";

export interface FleetState {
  advancedAnalyticVehiclesStatus: QueryStatus;
  driversHistoryStatus: QueryStatus;
  advancedAnalyticVehicles: AdvancedAnalyticsVehicles[];
  driversHistory: DriversHistory[];
  planningMonthlyRowsStatus: QueryStatus;
  planningMonthlyRows: PlanningsMonthlyRowsByCompanyResponse;
  monthlyReportsStatus: QueryStatus;
  monthlyReports: MonthlyReportApi[];
}

const initialState: FleetState = {
  advancedAnalyticVehiclesStatus: "idle",
  driversHistoryStatus: "idle",
  advancedAnalyticVehicles: [],
  driversHistory: [],
  planningMonthlyRowsStatus: "idle",
  planningMonthlyRows: [],
  monthlyReportsStatus: "idle",
  monthlyReports: [],
};

export const driversHistoryAsync = createAsyncThunk(
  "driversHistory/call",
  async (companyUuid: string, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    return (
      await axios.get<DriversHistory[]>(
        `drivers_history/company/${companyUuid}`,
      )
    ).data;
  },
);

export const driversHistoryByDepartmentAsync = createAsyncThunk(
  "driversHistoryByDepartment/call",
  async (departmentUuid: string) => {
    const axios = authAxios({ departmentUuid });
    return (
      await axios.get<DriversHistory[]>(
        `departments/drivers_history/department/${departmentUuid}`,
      )
    ).data;
  },
);

export const planningMonthlyRowsAsync = createAsyncThunk(
  "planningMonthlyRows/call",
  async (companyUuid: string) => {
    const axios = authAxios({ companyUuid });
    return (
      await axios.get<PlanningsMonthlyRowsByCompanyResponse>(
        `driver_plannings/monthly_rows?companyUuid=${companyUuid}`,
      )
    ).data;
  },
);

export const monthlyReportsListByCompanyAsync = createAsyncThunk(
  "monthlyReportsListByCompany/call",
  (companyUuid: string) => {
    void companyUuid;
    return [...MONTHLY_REPORTS_MOCK].sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  },
);

export const monthlyReportsListByDepartementAsync = createAsyncThunk(
  "monthlyReportsListByDepartement/call",
  (departmentUuid: string) => {
    void departmentUuid;
    return [...MONTHLY_REPORTS_MOCK].sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  },
);

export const advancedAnalyticVehiclesAsync = createAsyncThunk(
  "advancednalyticVehicles/call",
  async (companyUuid: string, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    const response = await axios.get<AdvancedAnalyticsVehiclesApi[]>(
      `vehicles_advanced_analytics/company/${companyUuid}`,
    );

    return response.data.map((d) => {
      const additionalCostPerKm =
        d.additionalCostPerKm &&
        Number((d.additionalCostPerKm / 100).toFixed(2));

      //DATE CALCULATION
      const contractDurationMonth = getContractDurationMonth(
        d.contractStartDate,
        d.contractEndDate,
      );

      const consumedContractDuration = getConsumedContractDurationMonth(
        d.contractStartDate,
        contractDurationMonth,
      );

      const remainingContractDuration = getRemainingContractDurationMonth(
        contractDurationMonth,
        consumedContractDuration,
      );

      //MILEAGE CALCULATION
      const mensualContractMileage = getContractMensualMileage(
        d.contractMileage,
        contractDurationMonth,
      );

      const mensualMileage = getMensualMileage(d.mileage, d.contractStartDate);

      const projectionEndOfContract = getProjectionEndOfContract(
        mensualMileage,
        contractDurationMonth,
      );
      const mileageVariationEndContract = getMileageVariationEndContract(
        d.contractMileage,
        projectionEndOfContract,
      );
      const additionalCostEndContract = getAdditionalCostEndContract(
        mileageVariationEndContract,
        additionalCostPerKm,
      );

      const monthlyMileageKmVariation = getMonthlyMileageDeviation(
        mensualMileage,
        mensualContractMileage,
      );
      const mileageDeviationPercentage = getMileageDeviationPercentage(
        mensualMileage,
        mensualContractMileage,
      );
      return {
        ...d,
        contractDurationMonth,
        mensualContractMileage,
        mensualMileage,
        mileageDeviation:
          mileageDeviationPercentage && mileageDeviationPercentage > 10
            ? ("OVERROLLING" as const)
            : mileageDeviationPercentage && mileageDeviationPercentage < -10
              ? ("UNDERROLLING" as const)
              : mileageDeviationPercentage
                ? ("CORRECT" as const)
                : null,
        monthlyMileageKmVariation,
        mileageDeviationPercentage,
        projectionEndOfContract,
        mileageVariationEndContract,
        additionalCostEndContract,
        additionalCostPerKm,
        consumedContractDuration,
        remainingContractDuration,
        contractStartDate: d.contractStartDate,
        contractEndDate: d.contractEndDate,
        driverName: d.driverName && capitalize(d.driverName),
        reference: d.reference?.toLocaleUpperCase() ?? null,
      };
    });
  },
);

export const advancedAnalyticVehiclesByDepartmentAsync = createAsyncThunk(
  "advancednalyticVehiclesByDepartment/call",
  async (departmentUuid: string) => {
    const axios = authAxios({ departmentUuid });
    const response = await axios.get<AdvancedAnalyticsVehiclesApi[]>(
      `departments/vehicles/advanced_analytics/department/${departmentUuid}`,
    );

    return response.data.map((d) => {
      const additionalCostPerKm =
        d.additionalCostPerKm &&
        Number((d.additionalCostPerKm / 100).toFixed(2));

      //DATE CALCULATION
      const contractDurationMonth = getContractDurationMonth(
        d.contractStartDate,
        d.contractEndDate,
      );

      const consumedContractDuration = getConsumedContractDurationMonth(
        d.contractStartDate,
        contractDurationMonth,
      );

      const remainingContractDuration = getRemainingContractDurationMonth(
        contractDurationMonth,
        consumedContractDuration,
      );

      //MILEAGE CALCULATION
      const mensualContractMileage = getContractMensualMileage(
        d.contractMileage,
        contractDurationMonth,
      );

      const mensualMileage = getMensualMileage(d.mileage, d.contractStartDate);

      const projectionEndOfContract = getProjectionEndOfContract(
        mensualMileage,
        contractDurationMonth,
      );
      const mileageVariationEndContract = getMileageVariationEndContract(
        d.contractMileage,
        projectionEndOfContract,
      );
      const additionalCostEndContract = getAdditionalCostEndContract(
        mileageVariationEndContract,
        additionalCostPerKm,
      );

      const monthlyMileageKmVariation = getMonthlyMileageDeviation(
        mensualMileage,
        mensualContractMileage,
      );
      const mileageDeviationPercentage = getMileageDeviationPercentage(
        mensualMileage,
        mensualContractMileage,
      );
      return {
        ...d,
        contractDurationMonth,
        mensualContractMileage,
        mensualMileage,
        mileageDeviation:
          mileageDeviationPercentage && mileageDeviationPercentage > 10
            ? ("OVERROLLING" as const)
            : mileageDeviationPercentage && mileageDeviationPercentage < -10
              ? ("UNDERROLLING" as const)
              : mileageDeviationPercentage
                ? ("CORRECT" as const)
                : null,
        monthlyMileageKmVariation,
        mileageDeviationPercentage,
        projectionEndOfContract,
        mileageVariationEndContract,
        additionalCostEndContract,
        additionalCostPerKm,
        consumedContractDuration,
        remainingContractDuration,
        contractStartDate: d.contractStartDate,
        contractEndDate: d.contractEndDate,
        driverName: d.driverName && capitalize(d.driverName),
        reference: d.reference?.toLocaleUpperCase() ?? null,
      };
    });
  },
);

export const fleetSlice = createSlice({
  name: "fleet",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    resetFleetListStatus(state) {
      state.advancedAnalyticVehiclesStatus = "idle";
    },
    resetDriversHistoryListStatus(state) {
      state.driversHistoryStatus = "idle";
    },
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder
      .addCase(advancedAnalyticVehiclesAsync.pending, (state) => {
        state.advancedAnalyticVehiclesStatus = "processing";
      })
      .addCase(advancedAnalyticVehiclesAsync.fulfilled, (state, action) => {
        state.advancedAnalyticVehicles = action.payload;
        state.advancedAnalyticVehiclesStatus = "success";
      })
      .addCase(advancedAnalyticVehiclesAsync.rejected, (state) => {
        state.advancedAnalyticVehiclesStatus = "failed";
      })
      .addCase(advancedAnalyticVehiclesByDepartmentAsync.pending, (state) => {
        state.advancedAnalyticVehiclesStatus = "processing";
      })
      .addCase(
        advancedAnalyticVehiclesByDepartmentAsync.fulfilled,
        (state, action) => {
          state.advancedAnalyticVehicles = action.payload;
          state.advancedAnalyticVehiclesStatus = "success";
        },
      )
      .addCase(advancedAnalyticVehiclesByDepartmentAsync.rejected, (state) => {
        state.advancedAnalyticVehiclesStatus = "failed";
      })
      .addCase(driversHistoryAsync.pending, (state) => {
        state.driversHistoryStatus = "processing";
      })
      .addCase(driversHistoryAsync.fulfilled, (state, action) => {
        state.driversHistory = action.payload;
        state.driversHistoryStatus = "success";
      })
      .addCase(driversHistoryAsync.rejected, (state) => {
        state.driversHistoryStatus = "failed";
      })
      .addCase(driversHistoryByDepartmentAsync.pending, (state) => {
        state.driversHistoryStatus = "processing";
      })
      .addCase(driversHistoryByDepartmentAsync.fulfilled, (state, action) => {
        state.driversHistory = action.payload;
        state.driversHistoryStatus = "success";
      })
      .addCase(driversHistoryByDepartmentAsync.rejected, (state) => {
        state.driversHistoryStatus = "failed";
      })
      .addCase(planningMonthlyRowsAsync.pending, (state) => {
        state.planningMonthlyRowsStatus = "processing";
      })
      .addCase(planningMonthlyRowsAsync.fulfilled, (state, action) => {
        state.planningMonthlyRows = action.payload;
        state.planningMonthlyRowsStatus = "success";
      })
      .addCase(planningMonthlyRowsAsync.rejected, (state) => {
        state.planningMonthlyRowsStatus = "failed";
      })
      .addCase(monthlyReportsListByCompanyAsync.pending, (state) => {
        state.monthlyReportsStatus = "processing";
      })
      .addCase(monthlyReportsListByCompanyAsync.fulfilled, (state, action) => {
        state.monthlyReports = action.payload;
        state.monthlyReportsStatus = "success";
      })
      .addCase(monthlyReportsListByCompanyAsync.rejected, (state) => {
        state.monthlyReportsStatus = "failed";
      })
      .addCase(monthlyReportsListByDepartementAsync.pending, (state) => {
        state.monthlyReportsStatus = "processing";
      })
      .addCase(
        monthlyReportsListByDepartementAsync.fulfilled,
        (state, action) => {
          state.monthlyReports = action.payload;
          state.monthlyReportsStatus = "success";
        },
      )
      .addCase(monthlyReportsListByDepartementAsync.rejected, (state) => {
        state.monthlyReportsStatus = "failed";
      });
  },
});

export const selectAdvancedAnalyticsVehiclesList = (state: RootState) =>
  state.fleet.advancedAnalyticVehicles;
export const selectAdvancedAnalyticsVehiclesListStatus = (state: RootState) =>
  state.fleet.advancedAnalyticVehiclesStatus;
export const selectDriversHistoryList = (state: RootState) =>
  state.fleet.driversHistory;
export const selectDriversHistoryListStatus = (state: RootState) =>
  state.fleet.driversHistoryStatus;
export const selectPlanningMonthlyRows = (state: RootState) =>
  state.fleet.planningMonthlyRows;
export const selectPlanningMonthlyRowsStatus = (state: RootState) =>
  state.fleet.planningMonthlyRowsStatus;
export const selectMonthlyReportsList = (state: RootState) =>
  state.fleet.monthlyReports;
export const selectMonthlyReportsListStatus = (state: RootState) =>
  state.fleet.monthlyReportsStatus;

export default fleetSlice.reducer;
